import Link, { LinkProps } from 'next/link';
import { FC, PropsWithChildren, AnchorHTMLAttributes } from 'react';

/**
 * TrailingLink   
 * -------------- 
 * 内部リンクの末尾に必ず `/` を付与し、不要な 308 リダイレクトを防止するユーティリティ。
 * 既存の `next/link` と同じ API (href, as, replace, scroll など) をそのまま利用できます。
 * 外部リンク (http/https/mailto/tel など) や ルート (`/`) は変換しません。  
 */
type OptionalLinkProps = Omit<Partial<LinkProps>, 'href'> & { href?: LinkProps['href'] };
type InternalProps = OptionalLinkProps & AnchorHTMLAttributes<HTMLAnchorElement>;

const TrailingLink: FC<PropsWithChildren<InternalProps>> = ({ href, children, className = '', ...rest }) => {
  const processHref = (value: LinkProps['href']): LinkProps['href'] => {
    // 1. URL オブジェクトの場合はそのまま返す
    if (typeof value !== 'string') {return value;}

    // 2. 外部リンク (スキーマ付き) は変換しない
    if (/^([a-z][a-z0-9+\-.]*:|#)/i.test(value)) {
      return value;
    }

    // 3. ルートはそのまま
    if (value === '/') {return value;}

    // 4. 末尾に `/` が付いていない場合のみ追加
    return value.endsWith('/') ? value : `${value}/`;
  };

  const processedHref = href ? processHref(href) : undefined;

  // href がない場合は span として表示
  if (!href) {
    return (
      <span className={className} {...rest}>
        {children}
      </span>
    );
  }

  return (
    <Link href={processedHref!} className={className} {...rest}>
      {children}
    </Link>
  );
};

export default TrailingLink; 